import React from 'react';

import Moment from 'moment';

import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {reduxForm, getFormValues, getFormMeta} from 'redux-form';

import {
  CellType,
} from '@grapecity/wijmo.grid';
import {
  FlexGridCellTemplate,
  FlexGridColumnGroup,
} from '@grapecity/wijmo.react.grid';

import {withStyles} from '@material-ui/core/styles';
import {
  Box,
  Divider,
  Grid,
  Menu,
  MenuItem,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import HelpIcon from '@material-ui/icons/Help';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import SaveIcon from '@material-ui/icons/Save';

import BackButton from '../../../atoms/Buttons/BackButton';
import DangerButton from '../../../atoms/Buttons/DangerButton';
import PositiveButton from '../../../atoms/Buttons/PositiveButton';
import MuiTooltip from '../../../atoms/MuiTooltip';

import CostTable, {HIDE_COST_REASON} from './molecules/costTable';
import CustomFlexGrid, {createCollectionView} from '../../../molecules/CustomFlexGrid';
import OrderInfoTable from './molecules/orderInfoTable';

import MainContainer from '../../../organisms/MainContainer';

import {drSideKoziCostOperations} from '../../../../reducks/drSideKoziCost';

import {
  API_CODE,
  dntNoFormatFromPole,
  downloadTyohyoFromS3,
  filterValidNWZgsyo,
  getGeneralPurposes,
  getGeneralPurposeValue,
  getResponseResult,
  getToDate,
  isSameContractDnt,
  senroNameFormatFromPole,
} from '../../../../common/common';
import {createSearchList} from '../../../../common/ComboParts';
import * as validateRule from '../../../validateRule';

import DntEditDialog from './organisms/dntEditDialog';
import KojiEditDialog from './organisms/kojiEditDialog';
import OnSiteRebuildTooltip from './atoms/onSiteRebuildTooltip';

import {CALCULATION_STATUS, KOJI_EXAMINATIONS, ON_SITE_REBUILD_UMU, getKoziOverview} from './data';
import {getMaxGroupNo, isDrSideKoziCostAdmin} from '..';

import SelectPoleNo from '../../../organisms/SelectPoleNo';

/** 電柱最大登録数 */
const MAX_DNT_NUM = 50;

/** 工事グループ番号の最大値 */
const MAX_GROUP_COUNT = 6;

const styles = (theme) => ({});

/**
 * 算定基準日・事業所の初期値(未設定の場合のみ適用するデフォルト値)を
 * フォームの初期値(initialValues)自体に織り込んだオブジェクトを返す。
 * @param {object} detailTitle 取得済みのDrSideKoziCostTitle__c(新規作成時はundefined)
 * @param {string} userNWZgsyoCode ログインユーザのNW事業所コード
 * @return {object} フォームの初期値
 */
const buildInitialValues = (detailTitle, userNWZgsyoCode) => {
  const nwZgsyoCode = detailTitle?.NWZgsyo__r?.Code__c ?? userNWZgsyoCode;
  return {
    ManuallyAdded__c: true,
    ...detailTitle,
    CostKizyunDate__c: detailTitle?.CostKizyunDate__c ?? getToDate(),
    ...(nwZgsyoCode ? {NWZgsyo__r: {Code__c: nwZgsyoCode}} : {}),
  };
};

/**
 * mapStateToProps内でinitialValuesの都度オブジェクトリテラルを生成すると、
 * ストア更新のたびに参照が変わりconnect()の再renderが連鎖し、
 * Maximum update depth exceededを引き起こすため、入力(detailTitle/
 * userNWZgsyoCode)が変化しない限り同一の参照を返すようにキャッシュする。
 * (mapStateToPropsをファクトリ関数にすることで、コンポーネントのインスタンスごとに
 * 専用のキャッシュを持たせている)
 * @return {Function} (state, ownProps) => props
 */
const mapStateToProps = () => {
  let cache;

  return (state, ownProps) => {
    const detailTitle = state.drSideKoziCost.detail?.DrSideKoziCostTitle__c;
    const userNWZgsyoCode = ownProps.userInfo.Account?.NWZgsyo__r?.Code__c;

    if (!cache || cache.detailTitle !== detailTitle ||
      cache.userNWZgsyoCode !== userNWZgsyoCode) {
      cache = {
        detailTitle,
        userNWZgsyoCode,
        initialValues: buildInitialValues(detailTitle, userNWZgsyoCode),
      };
    }

    return {
      recordId: state.drSideKoziCost.id,
      generalPurposeMap: state.common.generalPurposeMap,

      // 管理者判定(編集可否の判定等に使用)。新規作成時もAPIを呼ばずに判定できるよう、
      // APIのレスポンスではなく画面側(generalPurposeMapとuserInfo)で判定する
      isAdmin: isDrSideKoziCostAdmin(
          ownProps.userInfo, state.common.generalPurposeMap.NWDesignZgsyaCode),

      initialValues: cache.initialValues,
    };
  };
};

const mapDispatchToProps = {
  clearDetail: drSideKoziCostOperations.clearDetail,
  getDetail: drSideKoziCostOperations.getDetail,
  saveItem: drSideKoziCostOperations.saveItem,
  confirmItem: drSideKoziCostOperations.confirmItem,
  setId: drSideKoziCostOperations.setId,
};

/**
 * redux-formのgetFormMeta()の結果(フィールド名の階層構造をそのまま反映したネストオブジェクト)から、
 * touched状態のフィールド名(Fieldのname propと同じドット区切り表記)を再帰的に収集する。
 * @param {object} meta getFormMeta()の結果(またはその一部)
 * @param {string} prefix 再帰呼び出し用の現在のフィールド名prefix
 * @return {string[]} touched状態のフィールド名一覧
 */
const collectTouchedFieldNames = (meta, prefix = '') => {
  if (!meta) {
    return [];
  }

  if (typeof meta.touched === 'boolean') {
    return meta.touched ? [prefix] : [];
  }

  return Object.keys(meta).flatMap((key) =>
    collectTouchedFieldNames(meta[key], prefix ? `${prefix}.${key}` : key));
};

/**
 * 一覧(改造対象電柱)の必須項目(電柱情報入力ダイアログの必須項目と同一)と、
 * 対応する一覧の列binding名の対応。この画面から登録・編集する場合は電柱情報入力
 * ダイアログ経由で必ず入力されるが、Salesforce側で直接生成されたデータは初期値が
 * nullとなるため、確定時・一覧表示時に別途チェックする。
 */
const DNT_REQUIRED_FIELDS = [
  {field: 'DrSideKoziNecessity__c', binding: 'DrSideKoziNecessity'},
  {field: 'KoziGroup__c', binding: 'KoziGroup__c'},
  {field: 'DrSideKoziCostNecessity__c', binding: 'DrSideKoziCostNecessity'},
  {field: 'OnSiteRebuildUmu__c', binding: 'OnSiteRebuildUmu'},
];

/**
 * 一覧(改造対象電柱)1件分の必須項目が全て入力されているかを判定する。
 * @param {object} dnt 改造対象電柱(DrSideKoziTargetPole__c相当のオブジェクト)
 * @return {boolean} 必須項目が全て入力されている場合true
 */
const isDntRequiredFilled = (dnt) => {
  if (DNT_REQUIRED_FIELDS.some(({field}) => !dnt[field])) {
    return false;
  }

  // 改造工事費用要否が「不要」の場合、備考も必須
  if (dnt.DrSideKoziCostNecessity__c === '0' && !dnt.Remarks__c) {
    return false;
  }

  return true;
};

/**
 * 一覧(改造対象電柱)のWijmo CollectionView向けgetError本体。必須項目が未入力の
 * セルにエラーメッセージを返すことで、Wijmoの標準機能によりセル・行が赤く表示される。
 * 画面表示直後には表示させず、確定ボタン押下時にエラーがあった場合のみ表示したいため、
 * 呼び出し側(Templateコンポーネント)で表示可否を制御した上でこの関数に委譲する。
 * @param {object} item 改造対象電柱(DrSideKoziTargetPole__c相当のオブジェクト)
 * @param {string} propName プロパティ名(列のbinding)
 * @return {string|undefined} エラーメッセージ(問題なければundefined)
 */
const getDntError = (item, propName) => {
  const requiredField = DNT_REQUIRED_FIELDS.find(
      (target) => target.binding === propName)?.field;
  if (requiredField) {
    return item[requiredField] ? undefined : '必須入力です';
  }

  if (propName === 'Remarks__c') {
    return (item.DrSideKoziCostNecessity__c === '0' && !item.Remarks__c) ?
      '改造工事費用が不要の理由を入力してください' : undefined;
  }

  return undefined;
};

/**
 * 一覧(改造対象電柱)を「同一電柱」(isSameContractDnt)の単位でグルーピングする。
 * @param {object[]} poles 改造対象電柱一覧
 * @return {Array<object[]>} 同一電柱ごとにまとめたグループの配列
 */
const groupBySameContractDnt = (poles) => {
  const groups = [];
  for (const pole of poles) {
    const group = groups.find((g) => isSameContractDnt(g[0], pole));
    if (group) {
      group.push(pole);
    } else {
      groups.push([pole]);
    }
  }
  return groups;
};

/**
 * 一覧(改造対象電柱)のうち指定した1件について、元位置建替有無の整合性エラー
 * (同一電柱内で「無」とそれ以外が混在している、または「有(仮移設)」「有(元戻し)」
 * の件数が一致しない)があればエラーメッセージを返す。
 * @param {object} item 改造対象電柱(DrSideKoziTargetPole__c相当のオブジェクト)
 * @param {object[]} allPoles 一覧全体(比較対象)
 * @return {string|undefined} エラーメッセージ(問題なければundefined)
 */
const getOnSiteRebuildInconsistencyError = (item, allPoles) => {
  const group = groupBySameContractDnt(allPoles).find((g) => g.includes(item));
  if (!group) {
    return undefined;
  }

  const nones = group.filter(
      (p) => p.OnSiteRebuildUmu__c === ON_SITE_REBUILD_UMU.NONE);
  const temporaries = group.filter(
      (p) => p.OnSiteRebuildUmu__c === ON_SITE_REBUILD_UMU.TEMPORARY_MOVE);
  const restores = group.filter(
      (p) => p.OnSiteRebuildUmu__c === ON_SITE_REBUILD_UMU.RESTORE);

  // 「無」とそれ以外が混在している場合エラー
  if (nones.length > 0 && (temporaries.length > 0 || restores.length > 0)) {
    return '同一電柱内で元位置建替有無「無」とそれ以外が混在しています';
  }

  // 「有(仮移設)」「有(元戻し)」がペアになっていない場合エラー
  if (temporaries.length !== restores.length) {
    return '「有(仮移設)」と「有(元戻し)」の件数が一致しません';
  }

  return undefined;
};

/**
 * 一覧(改造対象電柱)の元位置建替有無に関する整合性チェック。
 * @param {object[]} poles 改造対象電柱一覧
 * @return {boolean} 整合性に問題がある電柱が含まれる場合true
 */
const hasOnSiteRebuildInconsistency = (poles) =>
  poles.some((pole) => !!getOnSiteRebuildInconsistencyError(pole, poles));

/**
 * 一覧(改造対象電柱)のうち指定した1件について、同一工事グループ内の
 * 重複電柱エラーがあればエラーメッセージを返す。
 * @param {object} item 改造対象電柱(DrSideKoziTargetPole__c相当のオブジェクト)
 * @param {object[]} allPoles 一覧全体(比較対象)
 * @return {string|undefined} エラーメッセージ(問題なければundefined)
 */
const getDuplicateGroupError = (item, allPoles) => {
  const sameGroupPoles = allPoles.filter(
      (p) => p.KoziGroup__c === item.KoziGroup__c);
  const hasDuplicate = groupBySameContractDnt(sameGroupPoles)
      .some((group) => group.length > 1 && group.includes(item));

  return hasDuplicate ? '同一工事グループに同一の電柱が複数存在します' : undefined;
};

/**
 * 一覧(改造対象電柱)の同一工事グループ内の重複電柱チェック。
 * @param {object[]} poles 改造対象電柱一覧
 * @return {boolean} 同一工事グループに同一電柱が複数存在する場合true
 */
const hasDuplicateDntInSameGroup = (poles) =>
  poles.some((pole) => !!getDuplicateGroupError(pole, poles));

/**
 * 一覧(改造対象電柱)に設定されている工事グループ番号が、1から連続しているかを判定
 * @param {object[]} poles 改造対象電柱一覧
 * @return {boolean} 歯抜けがある場合true
 */
const hasKoziGroupGap = (poles) => {
  const groupNumbers = [...new Set(poles.map((pole) => pole.KoziGroup__c))]
      .filter((groupNo) => groupNo != null)
      .sort((a, b) => a - b);

  return groupNumbers.some((groupNo, index) => groupNo !== index + 1);
};

/**
 * 一覧(改造対象電柱)の「手動追加」「工事内容」列に表示する文字列を、指定した電柱に設定する。
 * 列フィルタにも表示内容と同じ値が出るよう、セルテンプレートで加工せず表示用プロパティとして持たせる。
 * 手動追加・自由入力電柱番号・工事要否・工事内容を変更したときは、必ず呼び出すこと。
 * @param {object} pole 改造対象電柱(DrSideKoziTargetPole__c相当のオブジェクト。直接書き換える)
 */
const setPoleDisplayLabels = (pole) => {
  // 手動追加の場合、電柱選択画面で自由入力した電柱は未登録電柱、一覧から選択した電柱は台帳選択電柱と表示
  if (!pole.ManuallyAdded__c) {
    pole.ManuallyAdded = '';
  } else {
    pole.ManuallyAdded = pole.DntNoManualInput__c ? '〇(未登録電柱)' : '〇(台帳選択電柱)';
  }

  // 工事要否が「否」の場合はハイフン表示
  pole.KoziOverview = pole.DrSideKoziNecessity__c === '0' ? '－' : getKoziOverview(pole);
};

/**
 * 一覧(改造対象電柱)のうち指定した1件について、工事要否・改造工事費用要否と
 * 工事内容(モデル単価明細)の相関チェックエラーがあればエラーメッセージを返す。
 * @param {object} item 改造対象電柱(DrSideKoziTargetPole__c相当のオブジェクト)
 * @return {string|undefined} エラーメッセージ(問題なければundefined)
 */
const getKoziOverviewCorrelationError = (item) => {
  const hasDetails = (item.DrSideKoziTargetPoleDetails__r ?? []).length > 0;

  // 工事要否・改造工事費用要否がともに「要」なのに工事内容が未設定の場合エラー
  // (工事要否が「要」でも改造工事費用要否が「不要」の場合は、下記のチェックにより
  // 工事内容を設定できない(費用算定を行わないため)。両方が「要」の場合のみ必須とする)
  if (item.DrSideKoziNecessity__c === '1' &&
    item.DrSideKoziCostNecessity__c === '1' && !hasDetails) {
    return '工事要否・改造工事費用要否がともに「要」なのに工事内容が未設定です';
  }

  // 工事要否が「不要」なのに工事内容が設定されている場合エラー
  if (item.DrSideKoziNecessity__c === '0' && hasDetails) {
    return '工事要否が「不要」なのに工事内容が設定されています';
  }

  // 改造工事費用要否が「不要」なのに工事内容が設定されている場合エラー
  if (item.DrSideKoziCostNecessity__c === '0' && hasDetails) {
    return '改造工事費用要否が「不要」なのに工事内容が設定されています';
  }

  return undefined;
};

/**
 * 一覧(改造対象電柱)の工事要否・改造工事費用要否と工事内容の相関チェック。
 * @param {object[]} poles 改造対象電柱一覧
 * @return {boolean} 相関チェックに問題がある電柱が含まれる場合true
 */
const hasKoziOverviewCorrelationError = (poles) =>
  poles.some((pole) => !!getKoziOverviewCorrelationError(pole));

/**
 * 一覧(改造対象電柱)のうち指定した1件について、事業所相関チェックエラー
 * (画面で選択されている事業所(NWZgsyo__r.Code__c)以外の事業所の電柱である)が
 * あればエラーメッセージを返す。KMS-api側が電柱ごとのNWZgsyo__cをまだ返却していない
 * 場合(値が未設定)は判定不可のため対象外とする(API対応後に自動的に機能するようにするため)。
 * @param {object} item 改造対象電柱(DrSideKoziTargetPole__c相当のオブジェクト)
 * @param {string} selectedZgsyoCode 画面で選択されている事業所コード
 * @return {string|undefined} エラーメッセージ(問題なければundefined)
 */
const getDifferentZgsyoError = (item, selectedZgsyoCode) => {
  if (!selectedZgsyoCode) {
    return undefined;
  }

  const poleZgsyoCode = item.NWZgsyo__r?.Code__c;
  return (!!poleZgsyoCode && poleZgsyoCode !== selectedZgsyoCode) ?
    '選択中の事業所以外の電柱です' : undefined;
};

/**
 * 一覧(改造対象電柱)の事業所相関チェック。
 * @param {object[]} poles 改造対象電柱一覧
 * @param {string} selectedZgsyoCode 画面で選択されている事業所コード
 * @return {boolean} 選択事業所以外の電柱が含まれる場合true
 */
const hasDifferentZgsyoDnt = (poles, selectedZgsyoCode) =>
  poles.some((pole) => !!getDifferentZgsyoError(pole, selectedZgsyoCode));

/**
 * 改造工事費用算定 Template
 * 画面ID:1702
 *
 * @param {object} props
 * @return {JSX.Element} 改造工事費用算定画面
 */
const Template = (props) => {
  const {
    // mapStateToProps関連
    generalPurposeMap,
    isAdmin,
    recordId,

    // mapDispatchToProps関連
    clearDetail,
    getDetail,
    saveItem,
    confirmItem,
    setId,

    // Page関連
    showMessage,
    userInfo,

    // Redux-Form関連
    change,
    handleSubmit,
    touch,
    untouch,
    values,
    formMeta,
    dirty,
  } = props;

  /** CustomFlexGridのref */
  const gridRef = React.useRef();

  // 一覧のエラー(getDntError/整合性チェック)を表示するかどうか。画面表示直後には
  // 表示せず、確定ボタン押下時にエラーがあった場合のみ表示したいため、refで管理する
  // (CollectionViewのgetErrorはCollectionView生成時に固定されるため、
  // stateではなくrefの現在値を都度参照する関数を渡す)
  const showDntErrorsRef = React.useRef(false);

  // 確定時点で画面に選択されていた事業所コード(事業所相関チェック用)。
  // getDntErrorIfEnabledはCollectionView生成時に固定される関数のため、
  // useCallbackの依存配列でpropsの最新値を追従させることができない。
  // そのためrefに確定時の値を保持し、都度その時点の値を参照する
  const selectedZgsyoCodeRef = React.useRef(null);
  const getDntErrorIfEnabled = React.useCallback((item, propName) => {
    if (!showDntErrorsRef.current) {
      return undefined;
    }

    const requiredError = getDntError(item, propName);
    if (requiredError) {
      return requiredError;
    }

    const collectionView = gridRef.current?.getGrid()?.collectionView;
    const allPoles = collectionView?.sourceCollection ?? [];

    if (propName === 'OnSiteRebuildUmu') {
      return getOnSiteRebuildInconsistencyError(item, allPoles);
    }

    if (propName === 'KoziGroup__c') {
      return getDuplicateGroupError(item, allPoles);
    }

    if (propName === 'KoziOverview') {
      return getKoziOverviewCorrelationError(item);
    }

    if (propName === 'Dsp_SenroName__c') {
      return getDifferentZgsyoError(item, selectedZgsyoCodeRef.current);
    }

    return undefined;
  }, []);

  /** 一覧のエラー表示を有効化し、その場で再描画する(画面表示直後は非表示のため) */
  const showDntErrorsOnGrid = React.useCallback(() => {
    showDntErrorsRef.current = true;
    gridRef.current?.getGrid().invalidate();
  }, []);

  /** 手動追加フラグ */
  const isManuallyAdded = !!values?.ManuallyAdded__c;

  // 一覧(Wijmoグリッド)が、データ取得/保存成功以降に変更されたかどうか。
  // 一覧の変更はredux-formのdirtyでは確実に検知できないため個別に管理し、
  // 算定結果の非表示判定と「戻る」ボタンの警告表示判定に使用する
  const [isGridModified, setIsGridModified] = React.useState(false);

  // 既存データの取得に失敗した場合、参照専用にして操作不可にする
  const [isDataFetchFailed, setIsDataFetchFailed] = React.useState(false);

  /**
   * 改造工事費用算定詳細のレスポンスデータ(DrSideKoziCostTitle__c)を一覧(Wijmoグリッド)・
   * 工事グループ番号の最大値・算定結果表示欄の表示可否に反映する。
   * 初期表示時(下記useEffect)・保存/確定成功時(onClickSave/onClickConfirm)のいずれからも呼び出す。
   * フォーム自体の値(redux-form)は、mapStateToPropsのinitialValuesがdetail stateの変更を
   * 検知して自動的に再初期化される(enableReinitialize)ため、ここでの対応は不要。
   */
  const applyDetailData = React.useCallback((data) => {
    const grid = gridRef.current;
    const targetPoles = (data?.DrSideKoziTargetPoles__r ?? []).map((pole) => {
      const item = {...pole};
      setPoleDisplayLabels(item);
      return item;
    });

    // Reduxストア経由のgrid.setItems()はアイテムをObject.freezeするため、
    // 読み込んだ電柱情報をeditItem/commitEditで直接編集できるよう、
    // ストアを使わずCollectionViewを直接itemsSourceに設定する
    if (grid) {
      grid.getGrid().itemsSource =
        createCollectionView(targetPoles, true, {}, getDntErrorIfEnabled);

      // itemsSourceを丸ごと置き換えるため、以前選択されていた行のチェック状態
      // (grid.selectedItems)は新しいCollectionViewの行とは無関係になってしまう。
      // 明示的にクリアしないと、古い行を指したままのselectedItemsが残り、
      // 電柱情報入力/工事内容入力ボタンが誤って活性状態になったまま古い行を
      // 参照してエラーになる(チェック状態のリセット漏れ)ため、ここでクリアする
      grid.setAllSelected(false);
      grid.selectedItems = [];
      setCheckedCount(0);
    }

    setMaxGroupNo(getMaxGroupNo(targetPoles));
    setIsGridModified(false);
  }, [getDntErrorIfEnabled]);

  /** 保存/確定で取得済みの件名Id(再取得不要) */
  const savedIdRef = React.useRef(null);

  // 画面表示時処理
  React.useEffect(() => {
    // 新規作成の保存/確定でIdが採番された場合は、保存レスポンスで表示済みのため再取得しない
    if (recordId && recordId === savedIdRef.current) {
      return;
    }

    const fetchData = async () => {
      if (recordId) {
        const response = await getDetail(recordId);
        const result = getResponseResult(response);

        // エラー時にはメッセージを表示し、操作不可にする
        if (result.errorCode != API_CODE.SUCCESS) {
          showMessage({
            message: result.messages,
          });
          setIsDataFetchFailed(true);
          applyDetailData({});
          return;
        }

        const data = response.data.body.data?.DrSideKoziCostTitle__c;

        // データが取得できなかった場合、エラーメッセージを表示して操作不可にする
        if (!data) {
          showMessage({
            message: {
              id: 'CE0052',
              values: ['改造工事費用算定件名', '取得'],
            },
          });
          setIsDataFetchFailed(true);
          applyDetailData({});
          return;
        }

        applyDetailData(data);
      } else {
        // 新規登録時もストアを使わずitemsSourceを直接設定するため、空データで初期化する
        applyDetailData({});
      }
    };

    fetchData();
  }, [
    recordId,
    getDetail,
    applyDetailData,
    showMessage,
  ]);

  // 画面終了時処理
  React.useEffect(() => {
    const grid = gridRef.current;

    // 終了時に取得データをクリアする
    return () => {
      clearDetail();

      if (grid) {
        grid.getGrid().itemsSource = createCollectionView([]);
      }
    };
  }, [clearDetail]);

  // チェックボックスをチェックした件数」
  const [checkedCount, setCheckedCount] = React.useState(0);

  /** チェックボックスチェック時の処理 */
  const onChangeChecked = React.useCallback(() => {
    const grid = gridRef.current;
    if (!grid) {
      return;
    }

    // チェックボックスのチェック件数を保持
    setCheckedCount(grid.selectedItems.length);
  }, []);

  // グループ番号の最大数
  const [maxGroupNo, setMaxGroupNo] = React.useState(1);

  /**
   * 一覧(Wijmoグリッド)の変更内容を、フォームの値(DrSideKoziTargetPoles__r)と
   * 工事グループ番号の最大値(maxGroupNo)にまとめて反映する。
   * 一覧の内容を変更する処理(電柱情報入力/工事内容入力/電柱追加/電柱削除)の最後に
   * 必ず呼び出すこと(呼び出し漏れが起きないよう、処理箇所ごとに個別実装しない)。
   */
  const syncGridChanges = React.useCallback(() => {
    const grid = gridRef.current;
    if (!grid) {
      return;
    }

    // Wijmoの一覧データはeditItem/commitEdit等で既存オブジェクトを直接書き換えるため、
    // そのままフォームの値に設定すると、redux-formが「前回値」と「今回値」を比較する際に
    // 同一オブジェクト参照を見てしまい変更を検知できず、フォーム全体の再検証(バリデーション)が
    // 遅延してしまう。そのため、都度新しいオブジェクトとしてコピーしてから反映する。
    const items = grid.getSourceCollection().map((item) => ({...item}));
    change('DrSideKoziTargetPoles__r', items);
    setMaxGroupNo(getMaxGroupNo(items));
    setIsGridModified(true);
  }, [change]);

  /** 要否区分 コンボボックス項目 */
  const gmNecessity = React.useMemo(() => {
    return getGeneralPurposes(generalPurposeMap, 'NecessityCategory').map(
        (item) => ({code: item.Code__c, value: item.Name}));
  }, [generalPurposeMap]);

  /** 元位置建替有無 コンボボックス項目 */
  const gmOnSiteRebuild = React.useMemo(() => {
    return getGeneralPurposes(generalPurposeMap, 'OnSiteRebuildUmu').map(
        (item) => ({code: item.Code__c, value: item.Name}));
  }, [generalPurposeMap]);

  /** 電柱情報入力ダイアログ確定時処理 */
  const onDecideDntEdit = React.useCallback((formValues) => {
    const grid = gridRef.current;
    const flexGrid = grid.getGrid();
    const view = flexGrid.collectionView;

    // 一覧にダイアログで入力した値を設定する(コード値に対応する表示ラベルも合わせて更新する)
    const necessityLabel = (code) => gmNecessity.find((o) => o.code === code)?.value ?? '';
    const onSiteRebuildLabel = (code) => gmOnSiteRebuild.find((o) => o.code === code)?.value ?? '';

    const kariisetsuCode = ON_SITE_REBUILD_UMU.TEMPORARY_MOVE;
    const motodoshiCode = ON_SITE_REBUILD_UMU.RESTORE;
    const muCode = ON_SITE_REBUILD_UMU.NONE;

    // view.refresh()で一覧全体を再描画すると、行が再生成され
    // チェックボックスの選択状態が消えてしまうため、editItem/commitEditで
    // 行単位に更新し、更新後にreItemCheckedで選択状態を復元する
    const selectedItems = grid.selectedItems;
    const sourceItems = selectedItems.map((item) => view.items[item.dataIndex]);

    // ダイアログで変更対象としてチェックされた項目のみ一覧に反映する
    // (複数電柱選択時の一括変更。1件選択時は全項目がチェック済みとして渡される)
    const isTarget = (name) => !!formValues.checked?.[name];

    // 選択項目に「有(元戻し)」の電柱を含む場合は、確定時に元位置建替有無の値を
    // 設定しない(「有(元戻し)」は「有(仮移設)」から自動追加される項目であり、
    // 直接変更するとペア関係が崩れてしまうため)
    const skipOnSiteRebuild = !isTarget('OnSiteRebuildUmu__c') ||
      sourceItems.some((data) => data.OnSiteRebuildUmu__c === motodoshiCode);

    // 自動追加/自動削除の判定に使うため、上書き前の元位置建替有無を保持しておく
    const prevOnSiteRebuildMap = new Map(
        sourceItems.map((data) => [data, data.OnSiteRebuildUmu__c]));

    // 元位置建替有無に「有(仮移設)」を設定すると「有(元戻し)」の項目が自動追加されるため、
    // 追加を行う前に、追加後の件数が上限(50)を超えないか確認する
    if (!skipOnSiteRebuild &&
      formValues.OnSiteRebuildUmu__c === kariisetsuCode) {
      const allItems = grid.getSourceCollection();
      const addCount = sourceItems.filter((data) => !allItems.some((other) =>
        other !== data && other.OnSiteRebuildUmu__c === motodoshiCode &&
        isSameContractDnt(other, data))).length;

      if (allItems.length + addCount > MAX_DNT_NUM) {
        showMessage({
          message: {
            id: 'CE0055',
            values: [MAX_DNT_NUM],
          },
        });

        return;
      }
    }

    /** 一覧への反映処理 */
    const applyDntEdit = () => {
      for (const data of sourceItems) {
        view.editItem(data);

        if (isTarget('DrSideKoziNecessity__c')) {
          data.DrSideKoziNecessity__c = formValues.DrSideKoziNecessity__c;
          data.DrSideKoziNecessity =
            necessityLabel(formValues.DrSideKoziNecessity__c);
        }
        if (isTarget('KoziGroup__c')) {
          data.KoziGroup__c = formValues.KoziGroup__c;
        }
        if (isTarget('DrSideKoziCostNecessity__c')) {
          data.DrSideKoziCostNecessity__c =
            formValues.DrSideKoziCostNecessity__c;
          data.DrSideKoziCostNecessity =
            necessityLabel(formValues.DrSideKoziCostNecessity__c);
        }
        if (!skipOnSiteRebuild) {
          data.OnSiteRebuildUmu__c = formValues.OnSiteRebuildUmu__c;
          data.OnSiteRebuildUmu =
            onSiteRebuildLabel(formValues.OnSiteRebuildUmu__c);
        }
        if (isTarget('Remarks__c')) {
          data.Remarks__c = formValues.Remarks__c;
        }
        setPoleDisplayLabels(data);

        view.commitEdit();
      }

      // 元位置建替有無「有(仮移設)」の自動追加/自動削除(1702仕様書のアクション説明)
      // ・元位置建替有無に「有(仮移設)」が設定され、その電柱の「有(元戻し)」が存在しない場合、
      //   「有(元戻し)」の項目を自動で追加する(それ以外の項目はコピー元と同一)
      // ・「有(仮移設)」を「無」に変更した場合、その電柱の「有(元戻し)」の項目を自動で削除する
      if (!skipOnSiteRebuild) {
        for (const data of sourceItems) {
          const prevCode = prevOnSiteRebuildMap.get(data);
          const newCode = data.OnSiteRebuildUmu__c;
          const allItems = grid.getSourceCollection();
          const findPair = (code) => allItems.find((other) =>
            other !== data && other.OnSiteRebuildUmu__c === code &&
            isSameContractDnt(other, data));

          if (newCode === kariisetsuCode && !findPair(motodoshiCode)) {
            const newItem = view.addNew();
            Object.assign(newItem, data);
            delete newItem.Id;
            newItem.ManuallyAdded__c = true;
            newItem.KoziGroup__c =
              Math.min(data.KoziGroup__c + 1, MAX_GROUP_COUNT);
            newItem.OnSiteRebuildUmu__c = motodoshiCode;
            newItem.OnSiteRebuildUmu = onSiteRebuildLabel(motodoshiCode);

            // 工事内容はコピーしない
            newItem.DrSideKoziTargetPoleDetails__r = [];
            setPoleDisplayLabels(newItem);
            view.commitNew();
          } else if (prevCode === kariisetsuCode && newCode === muCode) {
            const pair = findPair(motodoshiCode);
            if (pair) {
              view.remove(pair);
            }
          }
        }
      }

      // 行の追加・削除で一覧が再構築されるため、選択状態の復元は
      // すべての一覧操作が終わった後にまとめて行う
      grid.reItemChecked(selectedItems);

      syncGridChanges();

      // ダイアログを閉じる
      setOpenDntEdit(false);
    };

    // 「有(仮移設)」を「無」に変更すると、ペアの「有(元戻し)」の項目が自動削除される。
    // 複数選択時は初期値が空のため変更前の値が分からず、また削除される項目は
    // 選択外で気づきにくいため、削除対象がある場合は(1件選択時も含めて)確認メッセージを表示する
    if (!skipOnSiteRebuild && formValues.OnSiteRebuildUmu__c === muCode) {
      const allItems = grid.getSourceCollection();
      const hasDeleteTarget = sourceItems.some((data) =>
        prevOnSiteRebuildMap.get(data) === kariisetsuCode &&
        allItems.some((other) => other !== data &&
          other.OnSiteRebuildUmu__c === motodoshiCode &&
          isSameContractDnt(other, data)));

      if (hasDeleteTarget) {
        showMessage({
          message: 'OC0243',
          // OK/キャンセルを選択させるため、確認ダイアログとして表示する
          type: 'confirm',
          action: applyDntEdit,
        });

        return;
      }
    }

    // 「無」を「有(仮移設)」に変更すると、ペアの「有(元戻し)」の項目が自動追加される。
    // 複数選択時は初期値が空のため変更前の値が分からず、また追加される項目は
    // 選択外で気づきにくいため、追加対象がある場合は(1件選択時も含めて)確認メッセージを表示する
    if (!skipOnSiteRebuild &&
      formValues.OnSiteRebuildUmu__c === kariisetsuCode) {
      const allItems = grid.getSourceCollection();
      const hasAddTarget = sourceItems.some((data) =>
        prevOnSiteRebuildMap.get(data) === muCode &&
        !allItems.some((other) => other !== data &&
          other.OnSiteRebuildUmu__c === motodoshiCode &&
          isSameContractDnt(other, data)));

      if (hasAddTarget) {
        showMessage({
          message: 'OC0244',
          // OK/キャンセルを選択させるため、確認ダイアログとして表示する
          type: 'confirm',
          action: applyDntEdit,
        });

        return;
      }
    }

    applyDntEdit();
  }, [syncGridChanges, showMessage, gmNecessity, gmOnSiteRebuild]);

  /** 工事内容入力ダイアログ確定時処理 */
  const onKojiDntEdit = React.useCallback((formValues) => {
    const grid = gridRef.current;
    const view = grid.getGrid().collectionView;

    // ダイアログでチェックしたモデル単価を抽出
    const checkedTypes = Object.keys(KOJI_EXAMINATIONS).filter(
        (type) => formValues?.[type]);

    // 選択している対象電柱の改造対象電柱明細を、チェックした項目で全件置き換える
    const selectedItems = grid.selectedItems;
    for (const item of selectedItems) {
      const data = view.items[item.dataIndex];
      view.editItem(data);

      data.DrSideKoziTargetPoleDetails__r = checkedTypes.map((type) => ({
        ModelUnitPrice__c: type,
      }));
      setPoleDisplayLabels(data);

      view.commitEdit();
    }
    grid.reItemChecked(selectedItems);

    syncGridChanges();

    // ダイアログを閉じる
    setOpenKojiEdit(false);
  }, [syncGridChanges]);


  /**
   * FlexGrid セルフォーマットハンドラー
   * @param {object} grid FlexGridオブジェクト
   * @param {object} event イベント
   */
  const onFormatItemHandler = React.useCallback((grid, event) => {
    const panel = event.panel;
    const cellType = panel.cellType;
    const row = grid.rows[event.row];
    const data = row?.dataItem;
    if (!data) {
      return;
    }

    // 入力項目の制御
    if (grid.cells === panel && cellType === CellType.Cell) {
      const col = event.getColumn();
      const binding = col.binding;
      const isKojiOff = data.DrSideKoziNecessity__c === '0';
      const isDashTargetColumn = ![
        'Dsp_SenroName__c',
        'Dsp_DntNo__c',
        'DntCategory',
        'DrSideKoziNecessity',
        'ManuallyAdded',
        'Remarks__c',
      ].includes(binding);

      // 工事要否が「否」のとき、対象列はハイフン表示
      if (isKojiOff && isDashTargetColumn) {
        event.cell.textContent = '－';
        wijmo.addClass(event.cell, 'centerPos');
      } else {
        wijmo.removeClass(event.cell, 'centerPos');
      }

      // 工事要否が「否」の場合、非活性
      if (isKojiOff) {
        wijmo.addClass(event.cell, 'wj-state-disabled');
        wijmo.addClass(event.cell, 'uneditableCell');
      }
    }

    // 行頭のチェックボックス制御
    // if (grid.rowHeaders === panel && cellType == CellType.RowHeader) {
    //   const checkboxCell = event.cell?.children?.[0];
    //   // 工事要否が「否」の場合、チェックボックスを非活性
    //   if (checkboxCell && data.DrSideKoziNecessity__c === '0') {
    //     wijmo.addClass(checkboxCell, 'wj-state-disabled');
    //     wijmo.addClass(event.cell, 'uneditableCell');

    //     const checkBox = checkboxCell.querySelector('input[type="checkbox"]');
    //     if (checkBox) {
    //       checkBox.disabled = true;
    //     }
    //   }
    // }

    // NOTE: 左上のチェックボックスの判定は下記
    // (grid.topLeftCells === panel && cellType == CellType.TopLeft)
  }, []);

  /**
   * モデル単価(工事内容: MODEL_UNIT_PRICE_CATEGORIESのkojiType)ごとの工事内容グループ。
   * 汎用マスタ「DrSideKoziType」のCode__c(kojiTypeに対応)→ValueString2__c(工事内容グループ)のマップ。
   * 工事内容入力ダイアログの排他制御(異なる工事内容グループの項目をチェック不可にする)に使用する。
   */
  const kojiTypeOverviewGroups = React.useMemo(() => {
    const groups = {};
    for (const item of getGeneralPurposes(generalPurposeMap, 'DrSideKoziType')) {
      groups[item.Code__c] = item.ValueString2__c;
    }
    return groups;
  }, [generalPurposeMap]);

  /** 工事グループ コンボボックス項目(現在の最大値+1(新規グループ作成可能)まで選択可能にする) */
  const gmGroup = React.useMemo(() => {
    const groupMax = Math.min(maxGroupNo + 1, MAX_GROUP_COUNT);
    const group = [];
    for (let i = 1; i <= groupMax; i++) {
      group.push({code: i, value: String(i)});
    }

    return group;
  }, [maxGroupNo]);

  /** ログインユーザに紐づくNW事業所コード */
  const userNWZgsyoCode = userInfo.Account?.NWZgsyo__r?.Code__c;

  // 確定日(ReceiveDate__c)が「本日の1年前」より前(厳密な未満。ちょうど1年前は対象外)の場合は
  // 参照専用とする。本来この期間を超えたデータは1701一覧の検索対象外となり表示されないが、
  // 表示中に期間を超えた等のケースに備え、本画面側でも参照専用として扱う。
  // 1701一覧側(KMS-api DrSideKoziCost.jsの_list)の検索条件(ReceiveDate__c >= 本日-1年を表示対象とする、
  // すなわちちょうど1年前は表示対象)と境界を一致させるため、日付単位でのisBefore判定にする
  // (moment().diff(..., 'years')は年数が切り捨てられ、ちょうど1年前も1と判定されてしまうため使わない)
  const isReceiveDateOverOneYear = !!values?.ReceiveDate__c &&
    new Moment(values.ReceiveDate__c).isBefore(new Moment().subtract(1, 'years'), 'day');

  // 管理者ではなく、かつ取得した改造工事費用算定件名のNW事業所がログインユーザのNW事業所と
  // 異なる場合に参照専用とする。新規作成時(recordId未設定)は対象外とする。
  // 既存データの取得に失敗した場合は、権限に関わらず参照専用にする
  const isReadOnly = isDataFetchFailed || isReceiveDateOverOneYear ||
    (!!recordId && !isAdmin && values?.NWZgsyo__r?.Code__c !== userNWZgsyoCode);

  // 算定結果(CostTable)を非表示にする理由。算定ステータスが算定済でない場合は常に非表示、
  // 算定済でも一覧の変更により算定結果が古くなっている場合は非表示にする
  // (表示する場合はnull)
  const isCalculationCompleted =
    values?.CalculationStatus__c === CALCULATION_STATUS.COMPLETED;
  const hideCostReason = !isCalculationCompleted ?
    HIDE_COST_REASON.NOT_CONFIRMED :
    (isGridModified ? HIDE_COST_REASON.DATA_CHANGED : null);

  /** 電柱情報入力ダイアログの選択肢 */
  const dntEditComboItems = React.useMemo(() => {
    return {
      necessity: gmNecessity,
      group: gmGroup,
      onSiteRebuild: gmOnSiteRebuild,
    };
  }, [
    gmGroup,
    gmNecessity,
    gmOnSiteRebuild,
  ]);

  /**
   * 汎用マスタ「NW事業所」の選択肢(事業所項目で使用)。
   */
  const zgsyoComboItems = React.useMemo(() => {
    const gmZgsyo = filterValidNWZgsyo(generalPurposeMap.NWZgsyo);
    return createSearchList(gmZgsyo, 'Code__c', 'Name', null, true);
  }, [generalPurposeMap]);

  /** 汎用マスタ「共架種別」の選択肢(共架種別項目で使用。List/index.jsと同一の項目) */
  const kyogaTypeComboItems = React.useMemo(() => {
    return createSearchList(generalPurposeMap.KyogaType, 'Code__c', 'Name', null, true);
  }, [generalPurposeMap]);

  /** 元位置建替有無列のヘッダーテンプレート(列見出し+ツールチップ) */
  const onSiteRebuildHeaderTemplate = React.useCallback(() =>
    <React.Fragment>
      元位置<br />建替有無
      <OnSiteRebuildTooltip />
    </React.Fragment>, []);

  /**
     * FlexGridセクション
     *
     * @return {JSX.Element}
     */
  const gridSection = React.useMemo(() => {
    const props = {
      useStore: false,
      rowHeaderType: 'check',
      frozenColumns: 3,
      filterOn: true,
      headersVisibility: 'All',
      allowDragging: 'Columns',
      allowSorting: 'MultiColumn',
      counterOn: false,
      AddDeleteOn: false,
      isReadOnly: true,
      style: {height: '100%'},
      checkedFunction: onChangeChecked,
      formatItemFunction: onFormatItemHandler,
    };
    return (
      <CustomFlexGrid ref={gridRef} {...props}>
        <FlexGridColumnGroup binding="Dsp_SenroName__c" header="線路名" width={170} />
        <FlexGridColumnGroup binding="Dsp_DntNo__c" header="電柱番号" width={100} />
        <FlexGridColumnGroup binding="DntCategory" header="電柱区分" width={100} />

        <FlexGridColumnGroup binding="ManuallyAdded" header="手動追加" width={130} align="left">
          <FlexGridCellTemplate cellType="ColumnHeader" autoSizeRows={false}
            template={(cell) =>
              <React.Fragment>
                {cell.col.header}
                <MuiTooltip content={
                  <React.Fragment>
                    ・この画面から追加した項目の場合「〇」が表示されます。<br />
                    ・電柱選択画面で検索して一覧から選択した項目は「台帳選択電柱」、<br />
                    自由入力した項目は「未登録電柱」と表示されます。<br />
                    ・手動追加の項目のみ削除できます。
                  </React.Fragment>
                }>
                  <HelpIcon fontSize="small" />
                </MuiTooltip>
              </React.Fragment>}
          />
        </FlexGridColumnGroup>

        <FlexGridColumnGroup header="電柱情報" align="center">
          <FlexGridColumnGroup binding="DrSideKoziNecessity" header="工事\n要否" width={80} />
          <FlexGridColumnGroup binding="KoziGroup__c" header="工事\nグループ" width={100} />
          <FlexGridColumnGroup binding="DrSideKoziCostNecessity" header="改造工事\n費用要否" width={100} />
          <FlexGridColumnGroup binding="OnSiteRebuildUmu" header="元位置建替有無" width={100}>
            <FlexGridCellTemplate cellType="ColumnHeader" autoSizeRows={false}
              template={onSiteRebuildHeaderTemplate}
            />
          </FlexGridColumnGroup>
        </FlexGridColumnGroup>

        <FlexGridColumnGroup binding="KoziOverview" header="工事内容" width={240} />
        <FlexGridColumnGroup binding="Remarks__c" header="備考" width={220} />

      </CustomFlexGrid>
    );
  }, [
    onChangeChecked,
    onFormatItemHandler,
    onSiteRebuildHeaderTemplate,
  ],
  );

  /** 電柱情報入力/工事内容入力 ボタンの非活性条件 */
  const disableEdit = React.useMemo(() => {
    // 選択項目がない場合は非活性
    return !checkedCount;
  }, [checkedCount]);

  /** 電柱情報入力 ボタン クリック時処理 */
  const [openDntEdit, setOpenDntEdit] = React.useState(false);
  const [dntEditInitialValues, setDntEditInitialValues] = React.useState();
  const onClickEditDnt = React.useCallback(() => {
    const grid = gridRef.current;
    const view = grid.getGrid().collectionView;

    // 選択項目のうち先頭の項目の値をダイアログの初期値にする
    // (複数選択時の一括変更モードでは、ダイアログ側で空に初期化される。
    // 参照専用時は複数選択でもこの値を表示する)
    const firstItem = grid.selectedItems[0];
    const data = view.items[firstItem.dataIndex];

    setDntEditInitialValues({
      DrSideKoziNecessity__c: data.DrSideKoziNecessity__c,
      KoziGroup__c: data.KoziGroup__c,
      DrSideKoziCostNecessity__c: data.DrSideKoziCostNecessity__c,
      OnSiteRebuildUmu__c: data.OnSiteRebuildUmu__c,
      Remarks__c: data.Remarks__c,
    });

    setOpenDntEdit(true);
  }, []);

  /** 工事内容入力 ボタン クリック時処理 */
  const [openKojiEdit, setOpenKojiEdit] = React.useState(false);
  const [kojiEditInitialValues, setKojiEditInitialValues] = React.useState();
  const onClickEditKoji = React.useCallback(() => {
    const grid = gridRef.current;
    const view = grid.getGrid().collectionView;

    // 選択項目のうち先頭の項目の既存明細のモデル単価をダイアログの初期値にする
    const firstItem = grid.selectedItems[0];
    const data = view.items[firstItem.dataIndex];
    const details = data.DrSideKoziTargetPoleDetails__r ?? [];

    const initialValues = {};
    for (const type of Object.keys(KOJI_EXAMINATIONS)) {
      initialValues[type] = details.some(
          (detail) => detail.ModelUnitPrice__c === type);
    }
    setKojiEditInitialValues(initialValues);

    setOpenKojiEdit(true);
  }, []);

  const [openSelectPoleNo, setOpenSelectPoleNo] = React.useState(false);
  const onClickSelectDnt = React.useCallback(() => {
    // 事業所未選択の場合はメッセージを表示し、電柱選択画面は表示しない
    if (!values?.NWZgsyo__r?.Code__c) {
      showMessage({
        message: {
          id: 'CE0027',
          values: ['事業所'],
        },
      });

      return;
    }

    // 登録済み件数が上限に達している場合はメッセージを表示し、電柱選択画面は表示しない
    const grid = gridRef.current;
    if (grid && grid.getSourceCollection().length >= MAX_DNT_NUM) {
      showMessage({
        message: {
          id: 'CE0055',
          values: [MAX_DNT_NUM],
        },
      });

      return;
    }

    setOpenSelectPoleNo(true);
  }, [values, showMessage]);

  const closeSelectDnt = React.useCallback(() => {
    setOpenSelectPoleNo(false);
  }, []);

  const onClickDeleteDnt = React.useCallback(() => {
    const grid = gridRef.current;

    for (const item of grid.selectedItems) {
      // 手動登録ではない電柱が含まれる場合はエラー
      if (!item.dataItem.ManuallyAdded__c) {
        showMessage({
          message: {
            id: 'CE0057',
            values: ['手動登録ではない項目が含まれる', '、削除'],
          },
        });

        return;
      }

      // 元位置建替有無が「有(仮移設)」「有(元戻し)」の電柱が含まれる場合はエラー(「有(仮移設)」を
      // 直接削除するとペアの「有(元戻し)」だけが残ってしまうため、ペア解消は
      // 電柱情報入力ダイアログでの「無」への変更(自動削除)経由に限定する)
      const onSiteRebuildUmu = item.dataItem.OnSiteRebuildUmu__c;
      if (
        onSiteRebuildUmu === ON_SITE_REBUILD_UMU.TEMPORARY_MOVE ||
        onSiteRebuildUmu === ON_SITE_REBUILD_UMU.RESTORE
      ) {
        showMessage({
          message: {
            id: 'CE0057',
            values: ['元位置建替が「有」の項目が含まれる', '、削除'],
          },
        });

        return;
      }
    }

    showMessage({
      message: {
        id: 'CC0005',
        values: ['削除'],
      },
      action: () => {
        const grid = gridRef.current;
        const flexGrid = grid.getGrid();
        const collectionView = flexGrid.collectionView;

        // 一覧画面から選択行削除(index指定で削除するため、index降順でソート)
        const sortedItems = grid.selectedItems.sort(
            (a, b) => b.index - a.index);

        collectionView.beginUpdate();
        for (const data of sortedItems) {
          collectionView.removeAt(data.dataIndex);
        }
        collectionView.endUpdate();

        // 選択していた行を削除したため、チェック状態をクリアする(削除後もgrid.selectedItems
        // に削除済みの行が残ったままになり、電柱情報入力/工事内容入力ボタンが誤って
        // 活性状態のままになってしまうため)
        grid.selectedItems = [];
        setCheckedCount(0);

        syncGridChanges();
      },
    });
  }, [showMessage, syncGridChanges]);

  /**
   * 改造工事費用算定件名の保存(一時保存/確定)APIを実行し、結果表示・画面再描画を行う。
   * @param {object} formValues フォームの値(保存対象の改造工事費用算定件名)
   * @param {boolean} isConfirm true=確定、false=一時保存
   */
  const executeCostTitleSave = React.useCallback(async (
      formValues, isConfirm,
  ) => {
    const operation = isConfirm ? confirmItem : saveItem;
    const response = await operation(formValues);
    const result = getResponseResult(response);

    // エラー時にはメッセージを表示して終了
    if (result.errorCode != API_CODE.SUCCESS) {
      showMessage({
        message: result.messages,
      });

      return;
    }

    const data = response.data.body.data?.DrSideKoziCostTitle__c ?? {};
    applyDetailData(data);

    // 新規作成時にリロードしても保存データを表示できるよう、採番されたIdを保持する
    if (data.Id) {
      savedIdRef.current = data.Id;
      setId(data.Id);
    }

    // 一時保存は完了メッセージを表示しない(確定のみ表示する)
    if (!isConfirm) {
      return;
    }

    showMessage({
      message: {
        id: 'CI0010',
        values: ['確定'],
      },
    });
  }, [showMessage, saveItem, confirmItem, applyDetailData, setId]);

  /**
   * 保存(一時保存)ボタン押下時処理。確定と異なり、フォーム全体の入力チェック(handleSubmit)は行わない。
   * 一時保存時は算定処理を行わないため、算定基準日・事業所も含め必須チェックは一切行わない。
   * ただし以下の項目は最大文字数のみ一時保存時もチェック対象のため、handleSubmitを介さずに
   * 個別にチェックし、エラーがあれば該当項目をtouched状態にして(エラー表示させて)保存を中断する。
   * - 申込共架事業者・設計担当者・電話番号・メモ欄・設計書番号：最大文字数チェックのみ
   *   (これらは確定時のみ必須のため、必須チェックは行わない。syncErrorsは配列内で最初に失敗した
   *   ルールのメッセージしか持てず必須/文字数のどちらが失敗したか区別できないため、
   *   validateRule.maxLengthを現在値に対して直接呼び出して判定する)
   * また、直前に確定ボタン押下でエラーになった場合、対象外の項目もtouched状態が残ってしまい
   * どの項目が一時保存のエラー対象か分かりにくくなるため、上記チェックでエラーとなった項目以外の
   * touched状態は解除する。一時保存は確認ダイアログを表示せず、そのまま実行する。
   */
  const onClickSave = React.useCallback(() => {
    const maxLengthCheckFields = [
      {name: 'OrderKyogaZgsya__c', maxLength: 50, value: values?.OrderKyogaZgsya__c},
      {name: 'GaisanDesignTanto__c', maxLength: 20, value: values?.GaisanDesignTanto__c},
      {name: 'Tel__c', maxLength: 11, value: values?.Tel__c},
      {name: 'Memo__c', maxLength: 255, value: values?.Memo__c},
      ...(values?.DrSideKoziCostResults__r ?? []).map((result, index) => ({
        name: `DrSideKoziCostResults__r[${index}].DrSideKoziDocumentNo__c`,
        maxLength: 10,
        value: result?.DrSideKoziDocumentNo__c,
      })),
    ];
    const errorFields = maxLengthCheckFields
        .filter(({maxLength: max, value}) => validateRule.maxLength(max)(value))
        .map(({name}) => name);

    const touchedFieldsToKeep = new Set(errorFields);
    const otherTouchedFields = collectTouchedFieldNames(formMeta)
        .filter((name) => !touchedFieldsToKeep.has(name));
    if (otherTouchedFields.length) {
      untouch(...otherTouchedFields);
    }

    if (errorFields.length) {
      touch(...errorFields);
      return;
    }

    executeCostTitleSave(values, false);
  }, [executeCostTitleSave, values, formMeta, touch, untouch]);

  /**
   * 確定ボタン押下時処理。handleSubmitでラップされ、フォーム全体の入力チェックを通過した場合のみ呼ばれる。
   * 確定は確認ダイアログを表示し、OK後に実行する。
   */
  const onClickConfirm = React.useCallback((formValues) => {
    // 前回の確定失敗時に表示されたエラー(セルの赤色表示)が、該当項目を修正した後も
    // 一部残ってしまうことがあるため、チェックを実行する前に一旦エラー表示をクリアし、
    // 再チェックの結果に応じて改めて表示し直す
    showDntErrorsRef.current = false;
    gridRef.current?.getGrid().invalidate();

    // 一覧(改造対象電柱)の必須項目チェック。フォームのField単位のvalidateとは異なり
    // 一覧はredux-formのFieldとして登録されていないため、確定時にここで別途チェックする
    const targetPoles = formValues.DrSideKoziTargetPoles__r ?? [];

    // 対象電柱が1件も登録されていない場合はエラー
    if (targetPoles.length === 0) {
      showMessage({
        message: {
          id: 'CE0057',
          values: ['対象電柱が1件も登録されていない', '、確定'],
        },
      });

      return;
    }

    if (targetPoles.some((dnt) => !isDntRequiredFilled(dnt))) {
      // 該当項目を一覧上でも赤く表示する
      showDntErrorsOnGrid();

      showMessage({
        message: {
          id: 'CE0057',
          values: ['必須項目が未入力の電柱が含まれる', '、確定'],
        },
      });

      return;
    }

    // 元位置建替有無の整合性チェック(同一電柱内で「無」とそれ以外が混在している、
    // または「有(仮移設)」「有(元戻し)」がペアになっていない場合はエラー)
    if (hasOnSiteRebuildInconsistency(targetPoles)) {
      showDntErrorsOnGrid();

      showMessage({
        message: {
          id: 'CE0057',
          values: ['元位置建替有無の整合性に問題がある', '、確定'],
        },
      });

      return;
    }

    // 同一工事グループ内の重複電柱チェック
    if (hasDuplicateDntInSameGroup(targetPoles)) {
      showDntErrorsOnGrid();

      showMessage({
        message: {
          id: 'CE0057',
          values: ['同一工事グループに同一の電柱が含まれる', '、確定'],
        },
      });

      return;
    }

    // 工事グループ番号の歯抜けチェック
    if (hasKoziGroupGap(targetPoles)) {
      showMessage({
        message: {
          id: 'CE0057',
          values: ['工事グループ番号が連続していない', '、確定'],
        },
      });

      return;
    }

    // 工事要否・改造工事費用要否と工事内容の相関チェック
    if (hasKoziOverviewCorrelationError(targetPoles)) {
      showDntErrorsOnGrid();

      showMessage({
        message: {
          id: 'CE0057',
          values: ['工事要否と工事内容に不整合がある', '、確定'],
        },
      });

      return;
    }

    // 事業所の相関チェック(選択事業所以外の電柱が含まれる場合エラー)
    selectedZgsyoCodeRef.current = formValues.NWZgsyo__r?.Code__c;
    if (hasDifferentZgsyoDnt(targetPoles, selectedZgsyoCodeRef.current)) {
      showDntErrorsOnGrid();

      showMessage({
        message: {
          id: 'CE0057',
          values: ['選択事業所以外の電柱が含まれる', '、確定'],
        },
      });

      return;
    }

    showMessage({
      message: {
        id: 'CC0005',
        values: ['確定'],
      },
      action: () => executeCostTitleSave(formValues, true),
    });
  }, [executeCostTitleSave, showMessage, showDntErrorsOnGrid]);

  // PDF出力ボタン、押下時表示メニューの制御
  const [pdfButton, setPdfButton] = React.useState(null);
  const pdfMenuOpen = Boolean(pdfButton);
  const handlePdfClick = React.useCallback((event) => {
    // 前回保存時から入力値に変更がある場合、または算定結果がそもそも非表示(未確定/古い)の場合は、
    // 古い(または存在しない)算定結果で帳票が出力されてしまうため、保存を促すメッセージを
    // 表示してメニューを開かない。dirtyはredux-form管理のフォーム値の変更しか検出できないため、
    // 一覧(Wijmoグリッド)の変更等を表すhideCostReasonも合わせてチェックする
    if (dirty || hideCostReason) {
      showMessage({message: 'OI0245'});
      return;
    }

    setPdfButton(event.currentTarget);
  }, [dirty, hideCostReason, showMessage]);

  const handlePdfClose = React.useCallback(() => {
    setPdfButton(null);
  }, []);

  /**
   * 帳票を1件ダウンロードする。失敗時はエラーメッセージを表示する。
   * @param {string} tyohyoName 帳票API名(Tyohyo/{tyohyoName})
   * @param {string} label エラーメッセージ用の帳票の表示名
   * @return {Promise<boolean>} 成功時はtrue、失敗時はfalse
   */
  const downloadTyohyo = React.useCallback(async (tyohyoName, label) => {
    const ret = await downloadTyohyoFromS3(tyohyoName, {Id: values?.Id});
    if (!ret) {
      showMessage({
        message: {
          id: 'CE0052',
          values: [label, 'ダウンロード'],
        },
      });
    }
    return ret;
  }, [values?.Id, showMessage]);

  /** 帳票出力(見積書) */
  const handleClickMitumori = React.useCallback(() => {
    handlePdfClose();
    downloadTyohyo('DrSideKoziCostMitumori', '見積書');
  }, [handlePdfClose, downloadTyohyo]);

  /** 帳票出力(工事内容) */
  const handleClickKoziContent = React.useCallback(() => {
    handlePdfClose();
    downloadTyohyo('DrSideKoziCostKoziContent', '工事内容');
  }, [handlePdfClose, downloadTyohyo]);

  /** 帳票出力(電柱明細) */
  const handleClickDntDetail = React.useCallback(() => {
    handlePdfClose();
    downloadTyohyo('DrSideKoziCostDntDetail', '電柱明細');
  }, [handlePdfClose, downloadTyohyo]);

  /** 帳票出力(全件一括: 見積書・工事内容・電柱明細をそれぞれ呼び出す) */
  const handleClickAllTyohyo = React.useCallback(async () => {
    handlePdfClose();
    await downloadTyohyo('DrSideKoziCostMitumori', '見積書');
    await downloadTyohyo('DrSideKoziCostKoziContent', '工事内容');
    await downloadTyohyo('DrSideKoziCostDntDetail', '電柱明細');
  }, [handlePdfClose, downloadTyohyo]);

  /**
   * フッターボタンセクション
   *
   * @return {JSX.Element}
   */
  const footerBtn = React.useMemo(() => {
    const pdfId = 'pdf-output';
    const pdfButtonId = `${pdfId}-button`;
    const pdfMenuId = `${pdfId}-menu`;

    return (
      <Grid container
        justifyContent="center"
        alignItems="flex-start"
        spacing={1}
      >
        {!isDataFetchFailed && (
          <>
            {!isReadOnly && (
              <Grid key="footer-btn1" item>
                <PositiveButton
                  startIcon={<AddIcon />}
                  onClick={onClickSelectDnt}
                  variant="contained"
                  size="large"
                  style={{minWidth: '120px'}}
                >
                  <span>電柱追加</span>
                </PositiveButton>
              </Grid>
            )}

            {!isReadOnly && (
              <Grid key="footer-btn2" item>
                <DangerButton
                  startIcon={<DeleteIcon />}
                  onClick={onClickDeleteDnt}
                  variant="contained"
                  size="large"
                  disabled={disableEdit}
                  style={{minWidth: '120px'}}
                >
                  <span>電柱削除</span>
                </DangerButton>
              </Grid>
            )}

            {!isReadOnly && (
              <Grid key="footer-btn3" item>
                <PositiveButton
                  onClick={onClickEditDnt}
                  variant="contained"
                  size="large"
                  disabled={disableEdit}
                >
                  <span>電柱情報入力</span>
                </PositiveButton>
              </Grid>
            )}

            <Grid key="footer-btn4" item>
              <PositiveButton
                onClick={onClickEditKoji}
                variant="contained"
                size="large"
                disabled={disableEdit}
              >
                <span>工事内容入力</span>
              </PositiveButton>
            </Grid>

            {!isReadOnly && (
              <Grid key="footer-btn5" item>
                <PositiveButton
                  startIcon={<SaveAltIcon />}
                  onClick={onClickSave}
                  variant="contained"
                  size="large"
                  disabled={false}
                  style={{minWidth: '140px'}}
                >
                  <span>保存</span>
                </PositiveButton>
              </Grid>
            )}

            {!isReadOnly && (
              <Grid key="footer-btn6" item>
                <PositiveButton
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit(onClickConfirm)}
                  variant="contained"
                  size="large"
                  disabled={false}
                  style={{minWidth: '140px'}}
                >
                  <span>確定</span>
                </PositiveButton>
              </Grid>
            )}

            <Grid key="footer-btn7" item>
              <PositiveButton
                id={pdfButtonId}
                endIcon={<KeyboardArrowDownIcon />}
                aria-controls={pdfMenuOpen ? pdfMenuId : undefined}
                aria-haspopup="true"
                aria-expanded={pdfMenuOpen}
                onClick={handlePdfClick}
                variant="contained"
                size="large"
                disabled={
                  values?.CalculationStatus__c !== CALCULATION_STATUS.COMPLETED
                }
                style={{minWidth: '120px'}}
              >
                <span>帳票出力</span>
              </PositiveButton>
            </Grid>

            <Menu
              id={pdfMenuId}
              anchorEl={pdfButton}
              open={pdfMenuOpen}
              onClose={handlePdfClose}
            >
              <MenuItem button onClick={handleClickMitumori}>
                <GetAppIcon />
                見積書
              </MenuItem>

              <MenuItem button onClick={handleClickKoziContent}>
                <GetAppIcon />
                工事内容
              </MenuItem>

              <MenuItem button onClick={handleClickDntDetail}>
                <GetAppIcon />
                電柱明細
              </MenuItem>

              <Divider />

              <MenuItem button onClick={handleClickAllTyohyo}>
                <GetAppIcon />
                全件一括
              </MenuItem>
            </Menu>
          </>
        )}

        <Grid key="footer-back" item>
          <BackButton
            props={{...props, doShowMessage: showMessage}}
            style={{minWidth: '140px'}}
            showWarning={true}
            isModified={isGridModified}
          />
        </Grid>
      </Grid>
    );
  }, [
    disableEdit,
    isReadOnly,
    isDataFetchFailed,
    isGridModified,
    pdfButton,
    pdfMenuOpen,
    props,
    values,
    showMessage,
    handlePdfClick,
    handlePdfClose,
    onClickDeleteDnt,
    onClickEditDnt,
    onClickEditKoji,
    onClickSave,
    onClickConfirm,
    onClickSelectDnt,
    handleSubmit,
    handleClickMitumori,
    handleClickKoziContent,
    handleClickDntDetail,
    handleClickAllTyohyo,
  ]);

  const setSelectedPoles = React.useCallback((dnts) => {
    if (!dnts || dnts.length === 0) {
      return;
    }

    // 自由入力時は文字数チェック
    for (const dnt of dnts) {
      const dntNoManualInput = dnt.DntNoManualInput__c;
      if (!dntNoManualInput) {
        // 自由入力ではない場合はスキップ
        continue;
      }

      // 自由入力した電柱番号の桁数チェック
      if (dntNoManualInput.length > 25) {
        showMessage({
          message: {
            id: 'CE0041',
            values: ['電柱番号'],
          },
        });

        return;
      }

      // 自由入力した線路名の桁数チェック
      if (dnt.SenroName__c?.length > 30) {
        showMessage({
          message: {
            id: 'CE0041',
            values: ['線路名'],
          },
        });

        return;
      }
    }

    // CustomFlexGridを取得
    const customGrid = gridRef.current;
    if (!customGrid) {
      return;
    }

    const listData = customGrid.getSourceCollection();

    // 登録最大件数を超える場合はエラーとする
    if (listData.length + dnts.length > MAX_DNT_NUM) {
      showMessage({
        message: {
          id: 'CE0055',
          values: [MAX_DNT_NUM],
        },
      });

      return;
    }

    // すでに追加されている電柱番号はエラーとする
    for (const data of listData) {
      const sameDnt = dnts.find((item) => isSameContractDnt(data, item));
      if (sameDnt) {
        showMessage({
          message: {
            id: 'CE0034',
            values: ['選択した電柱'],
          },
        });

        return;
      }
    }

    // 改造工事対象電柱 レコード形式に変換して一覧に設定
    const cv = customGrid.getGrid().collectionView;
    for (const dnt of dnts) {
      const newItem = cv.addNew();

      // Poleの要素をそのまま設定
      Object.assign(newItem, dnt);

      // NWZgsyo__c に値を設定し、元の参照コードを削除
      newItem.NWZgsyo__r = {Code__c: dnt.NWZgsyo__r_Code__c};
      delete newItem.NWZgsyo__r_Code__c;

      // 改造工事対象電柱 の初期値を設定(工事グループ・元位置建替有無・改造工事要否・
      // 改造工事費用要否は未設定(null)のまま追加し、ユーザに選択させる)
      newItem.ManuallyAdded__c = true; // 手動追加
      newItem.KoziGroup__c = null; // 工事グループ
      newItem.OnSiteRebuildUmu__c = null; // 元位置建替有無
      newItem.DrSideKoziNecessity__c = null; // 改造工事要否
      newItem.DrSideKoziCostNecessity__c = null; // 改造工事費用要否
      newItem.Remarks__c = ''; // 備考
      newItem.DrSideKoziUnitPriceDetail__r = []; // 改造工事単価明細 一覧

      // 表示用の文字列を初期化
      newItem.OnSiteRebuildUmu = '';
      newItem.DrSideKoziNecessity = '';
      newItem.DrSideKoziCostNecessity = '';

      // コード値を文字列に変換
      newItem.DntCategory = getGeneralPurposeValue(generalPurposeMap, 'DntCategory', dnt.DntCategory__c);
      newItem.Dsp_DntNo__c = dntNoFormatFromPole(dnt);
      newItem.Dsp_SenroName__c = senroNameFormatFromPole(dnt);
      setPoleDisplayLabels(newItem);
    }

    // 一覧に値を設定
    cv.commitNew();

    // 行の追加で一覧の行が再生成されるが、customGrid.selectedItemsは再生成前の行を
    // 指したまま更新されないため、チェックボックスチェンジイベントを発行し、
    // 一覧の実際のチェック状態からselectedItems・チェック件数を再設定する
    customGrid.onItemChecked();

    syncGridChanges();
  }, [
    generalPurposeMap,

    syncGridChanges,
    showMessage,
  ]);

  /** 電柱選択 */
  const selectPoleNo = React.useMemo(() => {
    return (
      <SelectPoleNo
        YbdsType="AM"
        modalOpen={openSelectPoleNo}
        setSelectedPole={setSelectedPoles}
        closeButton={closeSelectDnt}
        NWZgsyo={values.NWZgsyo__r?.Code__c}
      />
    );
  }, [
    openSelectPoleNo,
    values.NWZgsyo__r?.Code__c,
    closeSelectDnt,
    setSelectedPoles,
  ]);

  return (
    <form noValidate autoComplete="off">
      <MainContainer
        props={props}
        footerBtn={footerBtn}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)'}}>
          {/* 情報表示セクション */}
          <Box sx={{flex: '0 0 auto', display: 'flex', flexDirection: 'row', mb: 1}}>
            <Grid container
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={3}
            >
              <Grid item xs={12} sm={4}>
                <OrderInfoTable
                  isManuallyAdded={isManuallyAdded}
                  isAdmin={isAdmin}
                  readOnly={isReadOnly}
                  zgsyoComboItems={zgsyoComboItems}
                  kyogaTypeComboItems={kyogaTypeComboItems}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <CostTable
                  hideCostReason={hideCostReason}
                  readOnly={isReadOnly}
                />
              </Grid>
            </Grid>

          </Box>

          {/* 各電柱の情報および工事内容入力欄 */}
          <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto'}}>
            <Box sx={{height: '97%'}}>
              {gridSection}
            </Box>
          </Box>
        </Box>
      </MainContainer>

      <DntEditDialog
        selectedDntList={gridRef.current ? gridRef.current.selectedItems : []}
        disabled={false}
        open={openDntEdit}
        onOk={onDecideDntEdit}
        onCancel={() => setOpenDntEdit(false)}
        comboItems={dntEditComboItems}
        initialValues={dntEditInitialValues}
        readOnly={isReadOnly}
      />

      <KojiEditDialog
        selectedDntList={gridRef.current ? gridRef.current.selectedItems : []}
        disabled={false}
        open={openKojiEdit}
        onOk={onKojiDntEdit}
        onCancel={() => setOpenKojiEdit(false)}
        initialValues={kojiEditInitialValues}
        kojiTypeOverviewGroups={kojiTypeOverviewGroups}
        maxWidth="lg"
        fullWidth
        readOnly={isReadOnly}
      />

      {selectPoleNo}
    </form>
  );
};

Template.propTypes = {
  recordId: PropTypes.string,

  classes: PropTypes.object.isRequired,
  generalPurposeMap: PropTypes.object,
  isAdmin: PropTypes.bool,
  userInfo: PropTypes.object.isRequired,
  values: PropTypes.object,
  showMessage: PropTypes.func.isRequired,

  clearDetail: PropTypes.func.isRequired,
  getDetail: PropTypes.func.isRequired,
  saveItem: PropTypes.func.isRequired,
  confirmItem: PropTypes.func.isRequired,
  setId: PropTypes.func.isRequired,

  reset: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  touch: PropTypes.func.isRequired,
  untouch: PropTypes.func.isRequired,
  formMeta: PropTypes.object,
  dirty: PropTypes.bool,
};

const FORM_NAME = 'DrSideKoziCostDetail';

const FormedTemplate = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
  enableReinitialize: true,
})(connect((state) => {
  return {
    values: getFormValues(FORM_NAME)(state),
    formMeta: getFormMeta(FORM_NAME)(state),
  };
})(Template));

export default withStyles(styles)(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(FormedTemplate),
);
